import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { quotesApi } from '../api/quotes'
import type { QuoteCreate } from '../types'

const QUOTES_KEY = 'quotes'

export const useQuotes = (page = 1, pageSize = 20, tagId?: number) => {
  return useQuery({
    queryKey: [QUOTES_KEY, page, pageSize, tagId],
    queryFn: () => quotesApi.getQuotes(page, pageSize, tagId),
  })
}

export const useInfiniteQuotes = (pageSize = 20, tagId?: number, sourceId?: number) => {
  return useInfiniteQuery({
    queryKey: [QUOTES_KEY, 'infinite', pageSize, tagId, sourceId],
    queryFn: ({ pageParam }) => quotesApi.getQuotes(pageParam, pageSize, tagId, sourceId),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.page * lastPage.page_size
      return loaded < lastPage.total ? lastPage.page + 1 : undefined
    },
  })
}

export const useQuote = (id: number | null) => {
  return useQuery({
    queryKey: [QUOTES_KEY, id],
    queryFn: () => quotesApi.getQuote(id!),
    enabled: !!id,
  })
}

export const useCreateQuote = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: quotesApi.createQuote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUOTES_KEY] })
    },
  })
}

export const useUpdateQuote = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, quote }: { id: number; quote: Partial<QuoteCreate> }) =>
      quotesApi.updateQuote(id, quote),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUOTES_KEY, variables.id] })
      queryClient.invalidateQueries({ queryKey: [QUOTES_KEY] })
    },
  })
}

export const useDeleteQuote = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: quotesApi.deleteQuote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUOTES_KEY] })
    },
  })
}

export const useUploadImages = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ quoteId, files }: { quoteId: number; files: File[] }) =>
      quotesApi.uploadImages(quoteId, files),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUOTES_KEY, variables.quoteId] })
    },
  })
}
